import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Worker, Viewer, SpecialZoomLevel, LoadError } from '@react-pdf-viewer/core';
import { defaultLayoutPlugin } from '@react-pdf-viewer/default-layout';
import * as pdfjsLib from 'pdfjs-dist';
import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/default-layout/lib/styles/index.css';

interface SafePdfViewerProps {
  pdfUrl: string;
  filename?: string;
}

const SafePdfViewer: React.FC<SafePdfViewerProps> = ({ pdfUrl, filename = 'document.pdf' }) => {
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);
  const defaultLayoutPluginInstance = defaultLayoutPlugin();

  // Use jsDelivr CDN - most reliable for npm packages
  const workerUrl = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.js`;

  const renderErrorFallback = (error: LoadError) => {
    console.error('[SafePdfViewer] PDF Load Error:', error);
    return (
      <div className="flex items-center justify-center h-full bg-red-50 dark:bg-red-900/20">
        <div className="p-6 bg-white dark:bg-gray-800 rounded-lg border border-red-200 dark:border-red-800 max-w-sm text-center">
          <p className="text-red-700 dark:text-red-300 font-medium mb-2">{t('Failed to Render PDF')}</p>
          <p className="text-sm text-red-600 dark:text-red-400">{error.message}</p>
          <p className="text-xs text-red-500 dark:text-red-500 mt-2">{t('Check browser console for details')}</p>
        </div>
      </div>
    );
  };

  if (error) {
    return (
      <div className="flex items-center justify-center h-full bg-red-50 dark:bg-red-900/20">
        <div className="p-6 bg-white dark:bg-gray-800 rounded-lg border border-red-200 dark:border-red-800 max-w-sm">
          <p className="text-red-700 dark:text-red-300 font-medium mb-2">{t('Unable to Display PDF')}</p>
          <p className="text-sm text-red-600 dark:text-red-400 mb-3">{error}</p>
          <p className="text-xs text-red-500 dark:text-red-400 font-mono break-all">{filename}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full bg-white dark:bg-gray-900 overflow-hidden flex flex-col">
      <div className="flex-1 w-full overflow-auto custom-scrollbar-main">
        <Worker workerUrl={workerUrl}>
          <Viewer
            fileUrl={pdfUrl}
            plugins={[defaultLayoutPluginInstance]}
            defaultScale={SpecialZoomLevel.PageWidth}
            onDocumentLoad={() => {
              setError(null);
            }}
            renderError={renderErrorFallback}
          />
        </Worker>
      </div>
    </div>
  );
};

export default SafePdfViewer;
